import {
  ComponentPropsWithoutRef,
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import kebabCase from 'lodash/kebabCase';

import { Section, type SectionMetadata } from '@spotify/confidence-core-react';

import { useContentNavigationContext } from '../context';

import styles from '../ContentNavigation.module.css';

export type PageContentSectionProps = Omit<
  ComponentPropsWithoutRef<typeof Section.Root>,
  'registerSectionMetadata'
>;

export const ContentSection = forwardRef<
  HTMLDivElement,
  PageContentSectionProps
>(({ className, id, role = 'region', ...props }, ref): ReactElement => {
  const { registerSection } = useContentNavigationContext();
  const [sectionMetadata, setSectionMetadata] = useState<SectionMetadata>();
  const resolvedId =
    id ?? (sectionMetadata ? kebabCase(sectionMetadata.title) : undefined);
  const sectionElementRef = useRef<HTMLDivElement | null>(null);
  const registerSectionMetadata = useCallback(
    (metadata: SectionMetadata): (() => void) => {
      setSectionMetadata(metadata);
      return () =>
        setSectionMetadata(currentMetadata =>
          currentMetadata === metadata ? undefined : currentMetadata,
        );
    },
    [],
  );
  const setSectionRef = useCallback(
    (element: HTMLDivElement | null): void => {
      sectionElementRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [ref],
  );
  useEffect(() => {
    const element = sectionElementRef.current;
    if (!element || !resolvedId || !sectionMetadata) return undefined;

    const { title } = sectionMetadata;

    return registerSection(resolvedId, {
      element,
      title,
    });
  }, [registerSection, resolvedId, sectionMetadata]);
  return (
    <Section.Root
      {...props}
      ref={setSectionRef}
      aria-label={
        props['aria-labelledby']
          ? undefined
          : (props['aria-label'] ?? sectionMetadata?.title)
      }
      id={resolvedId}
      registerSectionMetadata={registerSectionMetadata}
      role={role}
      className={classNames(styles.section, className)}
    />
  );
});

ContentSection.displayName = 'Page.ContentSection';
